import * as core from '@actions/core';
import { randomUUID } from 'node:crypto';
import * as fs from 'node:fs';
import * as os from 'node:os';
import * as path from 'node:path';
import { readBoundedRegularFile } from './byte-budget';
import { safeRepoPath } from './utils';

const RUN_STATE_NAME = 'codex-review-run-storage';
const REVIEW_STATE_NAME = 'codex-review-review-storage';
const OWNERSHIP_MARKER = '.codex-review-owner.json';
const RUN_PREFIX = 'codex-review-';
const STATE_VERSION = 2;
const OWNERSHIP_MARKER_MAX_BYTES = 4096;

export interface RunStorage {
  runnerTemp: string;
  runRoot: string;
  tempDir: string;
  gitDir: string;
  outputDir: string;
  token: string;
}

interface RunStorageState {
  version: number;
  runnerTemp: string;
  runRoot: string;
  token: string;
  device: string;
  inode: string;
}

interface ReviewStorageState {
  version: number;
  workspace: string;
  reviewDir: string;
  token: string;
  device: string;
  inode: string;
}

interface DirectoryIdentity {
  device: string;
  inode: string;
}

export function createRunStorage({
  workspace,
  outputDir,
}: {
  workspace: string;
  outputDir: string | null;
}): RunStorage {
  const runnerTemp = resolveRunnerTemp();
  const runRoot = fs.mkdtempSync(path.join(runnerTemp, RUN_PREFIX));
  try {
    fs.chmodSync(runRoot, 0o700);
    const canonicalRunRoot = fs.realpathSync(runRoot);
    const token = randomUUID();
    const identity = directoryIdentity(canonicalRunRoot);
    writeOwnershipMarker(canonicalRunRoot, token);
    core.saveState(RUN_STATE_NAME, JSON.stringify({
      version: STATE_VERSION,
      runnerTemp,
      runRoot: canonicalRunRoot,
      token,
      ...identity,
    } satisfies RunStorageState));
    const tempDir = path.join(canonicalRunRoot, 'tmp');
    const gitDir = path.join(canonicalRunRoot, 'git');
    const defaultOutputDir = path.join(canonicalRunRoot, 'diagnostics');
    fs.mkdirSync(tempDir, { recursive: true, mode: 0o700 });
    fs.mkdirSync(gitDir, { recursive: true, mode: 0o700 });
    fs.mkdirSync(defaultOutputDir, { recursive: true, mode: 0o700 });

    return {
      runnerTemp,
      runRoot: canonicalRunRoot,
      tempDir,
      gitDir,
      outputDir: outputDir === null ? defaultOutputDir : path.resolve(workspace, outputDir),
      token,
    };
  } catch (error) {
    try {
      fs.rmSync(runRoot, { recursive: true, force: true, maxRetries: 3, retryDelay: 200 });
    } catch {
      // Preserve the initialization failure; post cleanup still has saved state when registration succeeded.
    }
    throw error;
  }
}

export function prepareOwnedReviewDirectory({
  workspace,
  reviewDir,
  token,
  callerOwnedOutputDir,
}: {
  workspace: string;
  reviewDir: string;
  token: string;
  callerOwnedOutputDir: string | null;
}): string {
  const canonicalWorkspace = canonicalExistingDirectory(workspace, 'GITHUB_WORKSPACE');
  const target = safeRepoPath(canonicalWorkspace, reviewDir);
  let createdIdentity: DirectoryIdentity | null = null;
  if (path.dirname(target) !== canonicalWorkspace) {
    throw new Error(`review-dir must name a direct child of GITHUB_WORKSPACE: ${reviewDir}`);
  }
  if (fs.existsSync(target)) {
    throw new Error(`Refusing to replace pre-existing review-dir: ${target}`);
  }
  if (
    callerOwnedOutputDir !== null &&
    (callerOwnedOutputDir === target || callerOwnedOutputDir.startsWith(target + path.sep))
  ) {
    throw new Error('output-dir must not be equal to or contained by review-dir because review-dir is removed during post-job cleanup.');
  }

  try {
    fs.mkdirSync(target, { mode: 0o700 });
    createdIdentity = directoryIdentity(target);
    const canonicalTarget = fs.realpathSync(target);
    assertContainedPath(canonicalWorkspace, canonicalTarget, 'review directory');
    writeOwnershipMarker(canonicalTarget, token);
    core.saveState(REVIEW_STATE_NAME, JSON.stringify({
      version: STATE_VERSION,
      workspace: canonicalWorkspace,
      reviewDir: canonicalTarget,
      token,
      ...createdIdentity,
    } satisfies ReviewStorageState));
    return canonicalTarget;
  } catch (error) {
    if (createdIdentity !== null) {
      try {
        quarantineAndRemoveDirectory({
          parent: canonicalWorkspace,
          target,
          expectedIdentity: createdIdentity,
          quarantinePrefix: '.codex-review-input-init-cleanup-',
        });
      } catch {
        // Preserve the initialization failure; no review subprocess has started yet.
      }
    }
    throw error;
  }
}

export function prepareOutputDirectory({
  outputDir,
  callerOwned,
  actionOwnedDirectories,
}: {
  outputDir: string;
  callerOwned: boolean;
  actionOwnedDirectories: string[];
}): string {
  fs.mkdirSync(outputDir, { recursive: true, mode: 0o700 });
  const canonicalOutput = canonicalExistingDirectory(outputDir, 'output-dir');
  if (callerOwned) {
    for (const directory of actionOwnedDirectories) {
      const canonicalOwned = canonicalExistingDirectory(directory, 'action-owned storage');
      if (canonicalOutput === canonicalOwned || canonicalOutput.startsWith(canonicalOwned + path.sep)) {
        throw new Error(
          `Caller-owned output-dir resolves inside action-owned storage that is removed during post-job cleanup: ${canonicalOutput}`,
        );
      }
    }
  }
  return canonicalOutput;
}

export function cleanupSavedStorage(): void {
  const failures: string[] = [];
  const reviewStateText = core.getState(REVIEW_STATE_NAME);
  if (reviewStateText !== '') {
    try {
      cleanupReviewStorage(parseReviewStorageState(reviewStateText));
    } catch (error) {
      failures.push(error instanceof Error ? error.message : String(error));
    }
  }

  const runStateText = core.getState(RUN_STATE_NAME);
  if (runStateText !== '') {
    try {
      cleanupRunStorage(parseRunStorageState(runStateText));
    } catch (error) {
      failures.push(error instanceof Error ? error.message : String(error));
    }
  }

  if (failures.length > 0) {
    throw new Error(`Codex Review cleanup failed: ${failures.join('; ')}`);
  }
}

function cleanupRunStorage(state: RunStorageState): void {
  if (!fs.existsSync(state.runRoot)) {
    return;
  }
  const currentRunnerTemp = resolveRunnerTemp();
  if (currentRunnerTemp !== state.runnerTemp) {
    throw new Error('RUNNER_TEMP changed before owned run storage could be cleaned');
  }
  if (path.dirname(state.runRoot) !== state.runnerTemp || !path.basename(state.runRoot).startsWith(RUN_PREFIX)) {
    throw new Error(`Refusing to clean unsafe run storage path: ${state.runRoot}`);
  }
  const entry = fs.lstatSync(state.runRoot);
  if (!entry.isDirectory() || entry.isSymbolicLink()) {
    throw new Error(`Refusing to clean replaced run storage path: ${state.runRoot}`);
  }
  const canonicalRunRoot = fs.realpathSync(state.runRoot);
  if (canonicalRunRoot !== state.runRoot || path.dirname(canonicalRunRoot) !== state.runnerTemp) {
    throw new Error(`Run storage escaped RUNNER_TEMP: ${state.runRoot}`);
  }
  quarantineAndRemoveOwnedDirectory({
    parent: state.runnerTemp,
    target: state.runRoot,
    token: state.token,
    expectedIdentity: state,
    quarantinePrefix: '.codex-review-run-cleanup-',
  });
}

function cleanupReviewStorage(state: ReviewStorageState): void {
  if (!fs.existsSync(state.reviewDir)) {
    return;
  }
  if (!fs.existsSync(state.workspace)) {
    throw new Error(`Workspace no longer exists for review-directory cleanup: ${state.workspace}`);
  }
  const canonicalWorkspace = canonicalExistingDirectory(state.workspace, 'saved workspace');
  if (canonicalWorkspace !== state.workspace) {
    throw new Error(`Workspace path changed before review-directory cleanup: ${state.workspace}`);
  }
  if (path.dirname(state.reviewDir) !== canonicalWorkspace) {
    throw new Error(`Saved review directory is not a direct workspace child: ${state.reviewDir}`);
  }
  quarantineAndRemoveOwnedDirectory({
    parent: canonicalWorkspace,
    target: state.reviewDir,
    token: state.token,
    expectedIdentity: state,
    quarantinePrefix: '.codex-review-input-cleanup-',
  });
}

function resolveRunnerTemp(): string {
  const configured = String(process.env.RUNNER_TEMP || '').trim();
  if (configured === '' && process.env.GITHUB_ACTIONS === 'true') {
    throw new Error('Codex Review requires RUNNER_TEMP on GitHub Actions runners.');
  }
  return canonicalExistingDirectory(configured || os.tmpdir(), 'RUNNER_TEMP');
}

function canonicalExistingDirectory(value: string, description: string): string {
  const resolved = path.resolve(value);
  let canonical: string;
  try {
    canonical = fs.realpathSync(resolved);
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    throw new Error(`Could not resolve ${description} directory ${resolved}: ${detail}`);
  }
  if (!fs.statSync(canonical).isDirectory()) {
    throw new Error(`${description} is not a directory: ${canonical}`);
  }
  return canonical;
}

function assertContainedPath(parent: string, target: string, description: string): void {
  if (target === parent || !target.startsWith(parent + path.sep)) {
    throw new Error(`${description} must be contained by ${parent}: ${target}`);
  }
}

function writeOwnershipMarker(directory: string, token: string): void {
  fs.writeFileSync(
    path.join(directory, OWNERSHIP_MARKER),
    `${JSON.stringify({ version: STATE_VERSION, token })}\n`,
    { encoding: 'utf8', mode: 0o600, flag: 'wx' },
  );
}

function quarantineAndRemoveOwnedDirectory({
  parent,
  target,
  token,
  expectedIdentity,
  quarantinePrefix,
}: {
  parent: string;
  target: string;
  token: string;
  expectedIdentity: DirectoryIdentity;
  quarantinePrefix: string;
}): void {
  quarantineAndRemoveDirectory({
    parent,
    target,
    expectedIdentity,
    quarantinePrefix,
    verifyAfterRename: (quarantine) => assertOwnershipMarker(quarantine, token),
  });
}

function quarantineAndRemoveDirectory({
  parent,
  target,
  expectedIdentity,
  quarantinePrefix,
  verifyAfterRename,
}: {
  parent: string;
  target: string;
  expectedIdentity: DirectoryIdentity;
  quarantinePrefix: string;
  verifyAfterRename?: (quarantine: string) => void;
}): void {
  if (path.dirname(target) !== parent) {
    throw new Error(`Refusing to clean directory outside its direct owner: ${target}`);
  }
  const initial = fs.lstatSync(target);
  if (!initial.isDirectory() || initial.isSymbolicLink()) {
    throw new Error(`Refusing to clean replaced owned directory: ${target}`);
  }
  assertDirectoryIdentity(target, expectedIdentity);
  if (fs.realpathSync(target) !== target) {
    throw new Error(`Owned directory path changed before cleanup: ${target}`);
  }
  const quarantine = path.join(parent, `${quarantinePrefix}${randomUUID()}`);
  fs.renameSync(target, quarantine);
  const moved = fs.lstatSync(quarantine);
  if (
    !moved.isDirectory() ||
    moved.isSymbolicLink() ||
    moved.dev !== initial.dev ||
    moved.ino !== initial.ino
  ) {
    throw new Error(`Owned directory identity changed while quarantining ${target}; refusing recursive deletion. Quarantine: ${quarantine}`);
  }
  if (fs.realpathSync(quarantine) !== quarantine) {
    throw new Error(`Quarantined directory escaped its owner: ${quarantine}`);
  }
  assertDirectoryIdentity(quarantine, expectedIdentity);
  verifyAfterRename?.(quarantine);
  fs.rmSync(quarantine, { recursive: true, force: true, maxRetries: 3, retryDelay: 200 });
}

function directoryIdentity(directory: string): DirectoryIdentity {
  const entry = fs.lstatSync(directory, { bigint: true });
  if (!entry.isDirectory() || entry.isSymbolicLink()) {
    throw new Error(`Expected a real directory: ${directory}`);
  }
  return {
    device: entry.dev.toString(),
    inode: entry.ino.toString(),
  };
}

function assertDirectoryIdentity(directory: string, expected: DirectoryIdentity): void {
  const actual = directoryIdentity(directory);
  if (actual.device !== expected.device || actual.inode !== expected.inode) {
    throw new Error(`Owned directory identity mismatch: ${directory}`);
  }
}

function assertOwnershipMarker(directory: string, token: string): void {
  const markerPath = path.join(directory, OWNERSHIP_MARKER);
  let marker: unknown;
  try {
    marker = JSON.parse(readBoundedRegularFile({
      file: markerPath,
      maxBytes: OWNERSHIP_MARKER_MAX_BYTES,
      description: 'ownership marker',
      limitName: 'ownership-marker limit',
    }).toString('utf8'));
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    throw new Error(`Could not verify ownership marker for ${directory}: ${detail}`);
  }
  if (
    !marker ||
    typeof marker !== 'object' ||
    (marker as { version?: unknown }).version !== STATE_VERSION ||
    (marker as { token?: unknown }).token !== token
  ) {
    throw new Error(`Ownership marker mismatch for ${directory}`);
  }
}

function parseRunStorageState(value: string): RunStorageState {
  const parsed = parseState(value, RUN_STATE_NAME);
  if (
    typeof parsed.runnerTemp !== 'string' ||
    typeof parsed.runRoot !== 'string' ||
    typeof parsed.token !== 'string' ||
    typeof parsed.device !== 'string' ||
    typeof parsed.inode !== 'string'
  ) {
    throw new Error(`Invalid ${RUN_STATE_NAME} state`);
  }
  return parsed as unknown as RunStorageState;
}

function parseReviewStorageState(value: string): ReviewStorageState {
  const parsed = parseState(value, REVIEW_STATE_NAME);
  if (
    typeof parsed.workspace !== 'string' ||
    typeof parsed.reviewDir !== 'string' ||
    typeof parsed.token !== 'string' ||
    typeof parsed.device !== 'string' ||
    typeof parsed.inode !== 'string'
  ) {
    throw new Error(`Invalid ${REVIEW_STATE_NAME} state`);
  }
  return parsed as unknown as ReviewStorageState;
}

function parseState(value: string, description: string): Record<string, unknown> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    throw new Error(`Could not parse ${description} state: ${detail}`);
  }
  if (!parsed || typeof parsed !== 'object' || (parsed as { version?: unknown }).version !== STATE_VERSION) {
    throw new Error(`Unsupported ${description} state`);
  }
  return parsed as Record<string, unknown>;
}
